import React, { useEffect, useState } from 'react';
import { normalizeSnoozeFor } from '../useNoticeboard.js';
import './UserFeedbackSurvey.css';

const emptyForm = {
  role: '',
  usedAiBefore: '',
  previousAiSoftware: '',
  onboardingRating: null,
  easeOfUseRating: null,
  supportRating: null,
  feedbackResponsivenessRating: null,
  workloadImpactRating: null,
  favouriteFeatures: '',
  requestedFeature: ''
};

const isFilled = (value) => value != null && String(value).trim() !== '';

const pickForm = (data = {}) =>
  Object.fromEntries(Object.keys(emptyForm).map(key => [key, data[key] ?? emptyForm[key]]));

const UserFeedbackSurveyContent = ({ notice, savedData, name, onComplete, onSnooze }) => {
  const alreadyResponded = Boolean(savedData);
  const [showForm, setShowForm] = useState(alreadyResponded);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const snoozeOptions = normalizeSnoozeFor(notice);

  useEffect(() => {
    setForm(pickForm(savedData || {}));
    setError('');
    setShowForm(Boolean(savedData));
  }, [savedData]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const missing =
      !isFilled(form.role) ||
      !isFilled(form.usedAiBefore) ||
      (form.usedAiBefore === 'yes' && !isFilled(form.previousAiSoftware)) ||
      form.onboardingRating == null ||
      form.easeOfUseRating == null ||
      form.supportRating == null ||
      form.feedbackResponsivenessRating == null ||
      form.workloadImpactRating == null ||
      !isFilled(form.favouriteFeatures);

    if (missing) {
      setError('Please complete all required questions before submitting.');
      return;
    }

    setError('');
    onComplete({
      ...pickForm(form),
      previousAiSoftware: form.usedAiBefore === 'yes' ? form.previousAiSoftware.trim() : ''
    });
  };

  if (!showForm) {
    return (
      <>
        <div className="user-feedback-preamble">
          <p className="noticeboard-copy">{name ? `Hi ${name},` : 'Hi,'}</p>
          <p className="noticeboard-copy">
            At <span className="user-feedback-brand">MedGenix Tech</span> we are keen to receive
            feedback on how we are doing so we can continue to refine MedPrompt to suit your needs.
          </p>
          <p className="noticeboard-copy">
            Whether you have already been with us for some time, or only recently started, we would
            value a few moments of yours to complete this questionnaire.
          </p>
          <p className="noticeboard-copy">
            Your answers will help us understand what is working well and where we can improve. It
            should only take a few minutes, and you can skip it if now is not a good time.
          </p>
          <p className="noticeboard-copy noticeboard-copy-signoff">
            {'Kind regards,\nDr Syed Akhtar\nCEO'}
          </p>
        </div>
        <div className="user-feedback-actions">
          {snoozeOptions.length > 0 ? (
            snoozeOptions.map((option) => (
              <button
                key={option.id || option.ms}
                type="button"
                className="header-button"
                onClick={() => onSnooze(option.ms)}
              >
                {option.label}
              </button>
            ))
          ) : (
            <button type="button" className="header-button" onClick={() => onComplete()}>
              Not now
            </button>
          )}
          <button type="button" className="header-button header-button-primary" onClick={() => setShowForm(true)}>
            Participate
          </button>
        </div>
      </>
    );
  }

  return (
    <form className="user-feedback-survey" onSubmit={handleSubmit}>
      <Field label="1. Is your role clinical or non-clinical?">
        <ChoiceRow
          options={[
            { id: 'clinical', label: 'Clinical' },
            { id: 'non-clinical', label: 'Non-clinical' }
          ]}
          selected={form.role}
          onSelect={(id) => setField('role', id)}
        />
      </Field>

      <Field label="2. Had you used an AI-based software for document management before? If so, which one?">
        <ChoiceRow
          options={[
            { id: 'yes', label: 'Yes' },
            { id: 'no', label: 'No' }
          ]}
          selected={form.usedAiBefore}
          onSelect={(id) => setField('usedAiBefore', id)}
        />
        {form.usedAiBefore === 'yes' && (
          <input
            type="text"
            className="user-feedback-input user-feedback-input--follow-up"
            placeholder="Which one?"
            value={form.previousAiSoftware}
            onChange={(e) => setField('previousAiSoftware', e.target.value)}
          />
        )}
      </Field>

      <RatingField
        label="3. Out of 10, how would you rate the onboarding process?"
        hint="10 = excellent, 5 = adequate/neutral, 0 = very poor"
        value={form.onboardingRating}
        onChange={(value) => setField('onboardingRating', value)}
      />
      <RatingField
        label="4. Out of 10, how easy to use do you find MedPrompt?"
        hint="10 = very easy, 5 = neither easy nor difficult, 0 = very difficult"
        value={form.easeOfUseRating}
        onChange={(value) => setField('easeOfUseRating', value)}
      />
      <RatingField
        label="5. Out of 10, how helpful have you found MedPrompt support when you have had an issue/query?"
        hint="N/A = not applicable, 10 = extremely helpful, 5 = neither helpful nor unhelpful, 0 = not at all helpful"
        value={form.supportRating}
        onChange={(value) => setField('supportRating', value)}
        includeNa
      />
      <RatingField
        label={
          <>
            6. Out of 10, how responsive has <span className="user-feedback-brand">MedGenix Tech</span> been to your feedback or feature requests?
          </>
        }
        ariaLabel="6. Out of 10, how responsive has MedGenix Tech been to your feedback or feature requests?"
        hint="N/A = not applicable, 10 = extremely responsive, 5 = neither responsive nor unresponsive, 0 = not at all responsive"
        value={form.feedbackResponsivenessRating}
        onChange={(value) => setField('feedbackResponsivenessRating', value)}
        includeNa
      />
      <RatingField
        label="7. Out of 10, how much of an impact has MedPrompt had on your workload?"
        hint="10 = highest positive impact, 5 = no impact/neutral, 0 = most negative impact"
        value={form.workloadImpactRating}
        onChange={(value) => setField('workloadImpactRating', value)}
      />

      <Field label="8. What features do you like the most?">
        <textarea
          className="user-feedback-textarea"
          rows={4}
          value={form.favouriteFeatures}
          onChange={(e) => setField('favouriteFeatures', e.target.value)}
        />
      </Field>

      <Field label="9. Is there a feature you would like to see developed?">
        <textarea
          className="user-feedback-textarea"
          rows={4}
          value={form.requestedFeature}
          onChange={(e) => setField('requestedFeature', e.target.value)}
        />
      </Field>

      {error && <p className="user-feedback-error">{error}</p>}

      <div className="user-feedback-actions">
        {!alreadyResponded && snoozeOptions.map((option) => (
          <button
            key={option.id || option.ms}
            type="button"
            className="header-button"
            onClick={() => onSnooze(option.ms)}
          >
            {option.label}
          </button>
        ))}
        <button type="submit" className="header-button header-button-primary">
          {savedData ? 'Update response' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="user-feedback-field">
    <label className="user-feedback-label">{label}</label>
    {hint && <p className="user-feedback-hint">{hint}</p>}
    {children}
  </div>
);

const ChoiceRow = ({ options, selected, onSelect }) => (
  <div className="user-feedback-choices">
    {options.map(option => (
      <button
        key={option.id}
        type="button"
        className={`header-button${selected === option.id ? ' header-button-primary' : ''}`}
        onClick={() => onSelect(option.id)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const NA_VALUE = 'na';

const RatingField = ({ label, hint, value, onChange, ariaLabel, includeNa = false }) => (
  <Field label={label} hint={hint}>
    <div className="user-feedback-rating" role="group" aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}>
      {includeNa && (
        <button
          type="button"
          className={`user-feedback-score user-feedback-score-na${value === NA_VALUE ? ' selected' : ''}`}
          onClick={() => onChange(NA_VALUE)}
        >
          N/A
        </button>
      )}
      {Array.from({ length: 11 }, (_, score) => (
        <button
          key={score}
          type="button"
          className={`user-feedback-score${value === score ? ' selected' : ''}`}
          onClick={() => onChange(score)}
        >
          {score}
        </button>
      ))}
    </div>
  </Field>
);

export default UserFeedbackSurveyContent;
